/**
 * AdminSystemEvents — Ecosystem-wide event timeline (backed by workflow_events).
 *
 * Mirrors the Document Audit Ledger layout: stat cards + filters + paginated
 * table. Includes a per-row "Replay" button that calls
 * `POST /api/admin/system-events/{id}/dispatch` (idempotent via dedup).
 *
 * Route: /admin/system-events
 */
import React, { useState, useMemo } from 'react';
import { useQuery, useMutation } from '@tanstack/react-query';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import {
  Activity, Filter, RefreshCw, Play, CheckCircle2, XCircle,
  Layers, Building2,
} from 'lucide-react';
import { getToken } from '@/lib/authToken';

const API = process.env.REACT_APP_BACKEND_URL;
const authH = () => ({ Authorization: `Bearer ${getToken()}` });

const StatCard = ({ label, value, icon: Icon, tone = 'slate', testid }) => {
  const tones = {
    slate: 'from-slate-500/15 to-slate-500/5 text-slate-300 border-slate-400/30',
    emerald: 'from-emerald-500/15 to-emerald-500/5 text-emerald-300 border-emerald-400/30',
    rose: 'from-rose-500/15 to-rose-500/5 text-rose-300 border-rose-400/30',
    violet: 'from-violet-500/15 to-violet-500/5 text-violet-300 border-violet-400/30',
  };
  return (
    <div data-testid={testid} className={`rounded-xl border bg-gradient-to-br p-4 ${tones[tone]}`}>
      <div className="flex items-center justify-between">
        <span className="text-xs uppercase tracking-wider opacity-80">{label}</span>
        <Icon className="h-4 w-4 opacity-70" />
      </div>
      <div className="mt-2 text-2xl font-semibold">{value ?? '—'}</div>
    </div>
  );
};

const fmtDate = (iso) => {
  if (!iso) return '—';
  try { return new Date(iso).toLocaleString(); } catch { return iso; }
};

export default function AdminSystemEvents() {
  const [filters, setFilters] = useState({
    event_type: '', workspace: '', status: '', tenant_id: '',
    from_date: '', to_date: '',
  });
  const [applied, setApplied] = useState(filters);
  const [limit] = useState(50);
  const [skip, setSkip] = useState(0);

  const queryString = useMemo(() => {
    const qs = new URLSearchParams();
    Object.entries(applied).forEach(([k, v]) => {
      if (v !== '' && v !== null && v !== undefined) qs.set(k, v);
    });
    qs.set('limit', String(limit));
    qs.set('skip', String(skip));
    return qs.toString();
  }, [applied, limit, skip]);

  const statsQ = useQuery({
    queryKey: ['sysevents-stats'],
    queryFn: async () => {
      const r = await axios.get(`${API}/api/admin/system-events/stats`, { headers: authH() });
      return r.data;
    },
  });

  const listQ = useQuery({
    queryKey: ['sysevents-list', queryString],
    queryFn: async () => {
      const r = await axios.get(`${API}/api/admin/system-events?${queryString}`, { headers: authH() });
      return r.data;
    },
    keepPreviousData: true,
  });

  const replayMut = useMutation({
    mutationFn: async (eventId) => {
      const r = await axios.post(`${API}/api/admin/system-events/${eventId}/dispatch`, {}, { headers: authH() });
      return r.data;
    },
    onSuccess: (data) => {
      const count = (data?.deliveries || []).length;
      toast.success(`Replayed · ${count} channel(s) dispatched`);
    },
    onError: (e) => toast.error(`Replay failed: ${e?.response?.data?.detail || e.message}`),
  });

  const apply = () => { setSkip(0); setApplied(filters); };
  const reset = () => {
    const empty = { event_type: '', workspace: '', status: '', tenant_id: '', from_date: '', to_date: '' };
    setFilters(empty); setApplied(empty); setSkip(0);
  };

  const items = listQ.data?.items || [];
  const total = listQ.data?.total || 0;
  const topTypes = statsQ.data?.by_event_type || [];

  return (
    <div className="min-h-screen bg-slate-950 p-6 text-slate-100" data-testid="admin-system-events-page">
      <div className="mx-auto max-w-7xl space-y-6">
        <div className="flex flex-col gap-2 sm:flex-row sm:items-end sm:justify-between">
          <div>
            <h1 className="flex items-center gap-3 text-3xl font-bold">
              <Activity className="h-7 w-7 text-violet-400" />
              System Events Timeline
            </h1>
            <p className="mt-1 text-sm text-slate-400">
              Ecosystem-wide event stream · backed by <code className="text-violet-300">workflow_events</code>.
              Replay to re-dispatch notifications (idempotent).
            </p>
          </div>
          <Button
            data-testid="sysevents-refresh-btn"
            variant="outline"
            onClick={() => { listQ.refetch(); statsQ.refetch(); }}
            className="border-slate-700 bg-slate-900 hover:bg-slate-800"
          >
            <RefreshCw className="mr-2 h-4 w-4" /> Refresh
          </Button>
        </div>

        <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
          <StatCard testid="stat-total" label="Total events" value={statsQ.data?.total} icon={Layers} tone="slate" />
          <StatCard testid="stat-dispatched" label="Dispatched" value={statsQ.data?.dispatched} icon={CheckCircle2} tone="emerald" />
          <StatCard testid="stat-failed" label="Failed" value={statsQ.data?.failed} icon={XCircle} tone="rose" />
          <StatCard testid="stat-types" label="Event types" value={topTypes.length} icon={Building2} tone="violet" />
        </div>

        <Card className="border-slate-800 bg-slate-900/60">
          <CardHeader className="pb-3">
            <CardTitle className="flex items-center gap-2 text-base">
              <Filter className="h-4 w-4 text-slate-400" /> Filters
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 gap-3 md:grid-cols-4">
              <div>
                <label className="text-xs text-slate-400">Event type</label>
                <Select
                  value={filters.event_type || 'all'}
                  onValueChange={(v) => setFilters((f) => ({ ...f, event_type: v === 'all' ? '' : v }))}
                >
                  <SelectTrigger data-testid="filter-eventtype" className="mt-1 border-slate-700 bg-slate-950">
                    <SelectValue placeholder="Any type" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Any type</SelectItem>
                    {topTypes.map((t) => (
                      <SelectItem key={t.event_type} value={t.event_type}>
                        {t.event_type} ({t.count})
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-xs text-slate-400">Workspace</label>
                <Select
                  value={filters.workspace || 'all'}
                  onValueChange={(v) => setFilters((f) => ({ ...f, workspace: v === 'all' ? '' : v }))}
                >
                  <SelectTrigger data-testid="filter-workspace" className="mt-1 border-slate-700 bg-slate-950">
                    <SelectValue placeholder="Any workspace" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Any workspace</SelectItem>
                    <SelectItem value="events">events</SelectItem>
                    <SelectItem value="school">school</SelectItem>
                    <SelectItem value="church">church</SelectItem>
                    <SelectItem value="bookshop">bookshop</SelectItem>
                    <SelectItem value="donations">donations</SelectItem>
                    <SelectItem value="pos">pos</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-xs text-slate-400">Status</label>
                <Select
                  value={filters.status || 'all'}
                  onValueChange={(v) => setFilters((f) => ({ ...f, status: v === 'all' ? '' : v }))}
                >
                  <SelectTrigger data-testid="filter-status" className="mt-1 border-slate-700 bg-slate-950">
                    <SelectValue placeholder="Any" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Any</SelectItem>
                    <SelectItem value="emitted">emitted</SelectItem>
                    <SelectItem value="dispatched">dispatched</SelectItem>
                    <SelectItem value="failed">failed</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-xs text-slate-400">Tenant ID</label>
                <Input
                  data-testid="filter-tenant"
                  value={filters.tenant_id}
                  onChange={(e) => setFilters((f) => ({ ...f, tenant_id: e.target.value }))}
                  placeholder="Filter by tenant"
                  className="mt-1 border-slate-700 bg-slate-950"
                />
              </div>
              <div>
                <label className="text-xs text-slate-400">From</label>
                <Input data-testid="filter-from" type="date" value={filters.from_date}
                  onChange={(e) => setFilters((f) => ({ ...f, from_date: e.target.value }))}
                  className="mt-1 border-slate-700 bg-slate-950" />
              </div>
              <div>
                <label className="text-xs text-slate-400">To</label>
                <Input data-testid="filter-to" type="date" value={filters.to_date}
                  onChange={(e) => setFilters((f) => ({ ...f, to_date: e.target.value }))}
                  className="mt-1 border-slate-700 bg-slate-950" />
              </div>
              <div className="flex items-end gap-2 md:col-span-2">
                <Button data-testid="filter-apply-btn" onClick={apply} className="bg-violet-600 hover:bg-violet-500">
                  Apply
                </Button>
                <Button data-testid="filter-reset-btn" onClick={reset} variant="outline"
                  className="border-slate-700 bg-slate-900 hover:bg-slate-800">
                  Reset
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border-slate-800 bg-slate-900/60">
          <CardHeader className="pb-3">
            <CardTitle className="text-base">
              Timeline {total ? <span className="ml-2 text-xs font-normal text-slate-400">({total} total)</span> : null}
            </CardTitle>
          </CardHeader>
          <CardContent className="overflow-x-auto">
            <table className="w-full text-left text-sm" data-testid="sysevents-table">
              <thead>
                <tr className="border-b border-slate-800 text-xs uppercase tracking-wider text-slate-400">
                  <th className="py-2 pr-4">When</th>
                  <th className="py-2 pr-4">Event type</th>
                  <th className="py-2 pr-4">Workspace</th>
                  <th className="py-2 pr-4">Entity</th>
                  <th className="py-2 pr-4">Listeners</th>
                  <th className="py-2 pr-4">Status</th>
                  <th className="py-2 pr-4"></th>
                </tr>
              </thead>
              <tbody>
                {listQ.isLoading && (
                  <tr><td colSpan={7} className="py-8 text-center text-slate-500">Loading…</td></tr>
                )}
                {!listQ.isLoading && items.length === 0 && (
                  <tr><td colSpan={7} className="py-8 text-center text-slate-500" data-testid="sysevents-empty">
                    No system events match these filters.
                  </td></tr>
                )}
                {items.map((row) => {
                  const listeners = row.listener_results || [];
                  const okCount = listeners.filter((l) => l.status === 'ok').length;
                  const failCount = listeners.filter((l) => l.status !== 'ok').length;
                  return (
                    <tr key={row.id} className="border-b border-slate-800/60 hover:bg-slate-900/40" data-testid="sysevent-row">
                      <td className="py-2 pr-4 text-slate-300">{fmtDate(row.created_at)}</td>
                      <td className="py-2 pr-4">
                        <div className="font-medium">{row.event_type}</div>
                        {row.payload?.subject_id && (
                          <div className="text-xs text-slate-500">subject: {row.payload.subject_id}</div>
                        )}
                      </td>
                      <td className="py-2 pr-4">
                        <Badge variant="outline" className="border-slate-700 text-slate-300">
                          {row.payload?.workspace || row.entity_type || 'unknown'}
                        </Badge>
                      </td>
                      <td className="py-2 pr-4 text-slate-400">
                        {row.entity_type || '—'} · {row.entity_id || '—'}
                      </td>
                      <td className="py-2 pr-4 text-slate-400">
                        <span className="text-emerald-400">{okCount} ok</span>
                        {failCount > 0 && <span className="text-rose-400 ml-2">{failCount} fail</span>}
                      </td>
                      <td className="py-2 pr-4">
                        {row.status === 'dispatched' && (
                          <span className="inline-flex items-center gap-1 text-emerald-400">
                            <CheckCircle2 className="h-4 w-4" /> dispatched
                          </span>
                        )}
                        {row.status === 'failed' && (
                          <span className="inline-flex items-center gap-1 text-rose-400">
                            <XCircle className="h-4 w-4" /> failed
                          </span>
                        )}
                        {row.status === 'emitted' && (
                          <span className="text-amber-400">emitted</span>
                        )}
                      </td>
                      <td className="py-2 pr-4">
                        <Button
                          data-testid={`replay-btn-${row.id}`}
                          size="sm"
                          variant="outline"
                          className="border-slate-700 bg-slate-900 hover:bg-slate-800"
                          disabled={replayMut.isPending}
                          onClick={() => replayMut.mutate(row.id)}
                        >
                          <Play className="mr-1 h-3 w-3" /> Replay
                        </Button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </CardContent>
        </Card>

        <div className="flex items-center justify-between text-sm text-slate-400">
          <div>Showing {items.length ? skip + 1 : 0}–{skip + items.length} of {total}</div>
          <div className="flex gap-2">
            <Button data-testid="sysevents-prev-btn" variant="outline" className="border-slate-700 bg-slate-900 hover:bg-slate-800"
              disabled={skip === 0} onClick={() => setSkip((s) => Math.max(0, s - limit))}>
              Previous
            </Button>
            <Button data-testid="sysevents-next-btn" variant="outline" className="border-slate-700 bg-slate-900 hover:bg-slate-800"
              disabled={skip + items.length >= total} onClick={() => setSkip((s) => s + limit)}>
              Next
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
